import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "@/lib/router-compat";

/**
 * Resolve an anchor hash to an element without throwing. OAuth returns put
 * tokens/errors in the hash (#access_token=…, #error=…), which are not valid
 * CSS selectors — querySelector would throw and crash the whole page.
 */
function findHashTarget(rawHash: string): Element | null {
  const id = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
  if (!id || id.includes('=')) return null;
  let decoded = id;
  try { decoded = decodeURIComponent(id); } catch { /* keep raw */ }
  const byId = document.getElementById(decoded);
  if (byId) return byId;
  try {
    return document.querySelector(`#${CSS.escape(decoded)}`);
  } catch {
    return null;
  }
}


export function ScrollToTop() {
  const { pathname, search, hash } = useLocation();
  const prevPathRef = useRef<string>(pathname + search);
  const observerRef = useRef<MutationObserver | null>(null);
  // The router shim can't tell POP from PUSH, so detect real back/forward
  // navigation from the browser's popstate event.
  const popRef = useRef(false);

  // Take full control of scroll restoration
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    const onPop = () => { popRef.current = true; };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Continuously save scroll position for the current path (throttled via rAF),
  // so back-navigation can restore exactly where the user left off — globally.
  useEffect(() => {
    const key = `scroll:${pathname}${search}`;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        try {
          sessionStorage.setItem(key, String(window.scrollY));
        } catch {}
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname, search]);


  const getSaved = (path: string) => {
    try {
      const v = sessionStorage.getItem(`scroll:${path}`);
      return v ? parseInt(v, 10) : 0;
    } catch {
      return 0;
    }
  };

  const save = (path: string, y: number) => {
    try {
      sessionStorage.setItem(`scroll:${path}`, String(y));
    } catch {}
  };

  const disableAnchor = () => {
    try {
      document.documentElement.style.setProperty('overflow-anchor', 'none');
      document.body.style.setProperty('overflow-anchor', 'none');
    } catch {}
  };

  const enableAnchor = () => {
    try {
      document.documentElement.style.removeProperty('overflow-anchor');
      document.body.style.removeProperty('overflow-anchor');
    } catch {}
  };

  useLayoutEffect(() => {
    const prevPath = prevPathRef.current;
    const currentFull = pathname + search;

    if (currentFull !== prevPath) {
      // Cleanup any existing observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // Check for hash in URL (anchor links)
      const currentHash = hash || window.location.hash;

      // Batch all layout reads first to avoid forced reflows
      const currentScrollY = window.scrollY;
      const savedY = getSaved(currentFull);
      const returnToPath = sessionStorage.getItem('returnToPath');
      const returnToId = sessionStorage.getItem('returnToId');

      // Save previous page position (with query) for back-navigation
      save(prevPath, currentScrollY);

      const getHeaderOffset = () => {
        const header = document.querySelector('header') as HTMLElement | null;
        return header ? header.offsetHeight + 8 : 0;
      };

      const scrollIntoViewWithOffset = (element: Element, offset = 0) => {
        const y = (element as HTMLElement).getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

      const scrollToHash = () => {
        if (currentHash) {
          const element = findHashTarget(currentHash);
          if (element) {
            setTimeout(() => {
              scrollIntoViewWithOffset(element, getHeaderOffset());
            }, 80);
            return true;
          }
        }
        return false;
      };

      const clearAnchor = () => {
        try {
          sessionStorage.removeItem('returnToPath');
          sessionStorage.removeItem('returnToId');
        } catch {}
      };

      const tryAnchor = (): boolean => {
        // Match if returnToPath matches current path (with or without search params)
        const pathMatches = returnToPath === currentFull || 
                           returnToPath === pathname ||
                           returnToPath?.startsWith(pathname);
        
        if (pathMatches && returnToId) {
          const el = document.querySelector(`[data-listing-id="${returnToId}"]`) as HTMLElement | null;
          if (el) {
            setTimeout(() => {
              const headerOffset = getHeaderOffset();
              const elementPosition = el.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.scrollY - headerOffset - 20;
              
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
              
              // Highlight the card briefly
              el.style.transition = 'box-shadow 0.3s ease';
              el.style.boxShadow = '0 0 0 3px hsl(var(--primary) / 0.5)';
              setTimeout(() => {
                el.style.boxShadow = '';
              }, 1500);
              
              clearAnchor();
              enableAnchor();
            }, 150);
            return true;
          }
        }
        return false;
      };

      // Handle navigation based on type
      const isPop = popRef.current;
      popRef.current = false;

      if (isPop) {
        // Back/forward navigation - restore exact position
        requestAnimationFrame(() => {
          disableAnchor();
          
          // Try immediate anchor scroll first to exact card
          if (tryAnchor()) {
            prevPathRef.current = currentFull;
            return;
          }

          // Fallback to saved Y position — retry while async content mounts
          // and grows the page, unless the user scrolls themselves.
          if (savedY > 0) {
            let userScrolled = false;
            const onUserScroll = () => { userScrolled = true; };
            window.addEventListener('wheel', onUserScroll, { passive: true, once: true });
            window.addEventListener('touchstart', onUserScroll, { passive: true, once: true });
            const restore = () => {
              if (userScrolled) return;
              if (Math.abs(window.scrollY - savedY) > 2) window.scrollTo(0, savedY);
            };
            restore();
            [50, 150, 300, 600, 900, 1400].forEach((d) => setTimeout(restore, d));
            setTimeout(() => {
              window.removeEventListener('wheel', onUserScroll);
              window.removeEventListener('touchstart', onUserScroll);
            }, 1500);
          }

          // Use MutationObserver to detect when the target card element appears
          const shouldObserve = returnToId && (
            returnToPath === currentFull || 
            returnToPath === pathname ||
            returnToPath?.startsWith(pathname)
          );
          
          if (shouldObserve) {
            let attempts = 0;
            const maxAttempts = 20;
            
            // Try multiple times with increasing delays
            const retryAnchor = () => {
              attempts++;
              if (tryAnchor()) {
                if (observerRef.current) {
                  observerRef.current.disconnect();
                  observerRef.current = null;
                }
                return;
              }
              if (attempts < maxAttempts) {
                setTimeout(retryAnchor, 200);
              } else {
                enableAnchor();
              }
            };
            
            // Start retry loop
            setTimeout(retryAnchor, 200);
            
            observerRef.current = new MutationObserver(() => {
              if (tryAnchor() && observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
              }
            });

            observerRef.current.observe(document.body, {
              childList: true,
              subtree: true,
            });

            // Cleanup after 5 seconds if element never appears
            setTimeout(() => {
              if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
                enableAnchor();
              }
            }, 5000);
          } else {
            enableAnchor();
          }
        });
      } else {
        // PUSH/REPLACE navigation - scroll to top or hash
        clearAnchor();
        disableAnchor();

        if (!scrollToHash()) {
          scrollToTop();
          // Re-assert scroll-to-top after lazy content mounts and changes layout
          requestAnimationFrame(() => {
            scrollToTop();
            requestAnimationFrame(scrollToTop);
          });
          setTimeout(scrollToTop, 50);
          setTimeout(scrollToTop, 150);
          setTimeout(scrollToTop, 400);
          setTimeout(() => { scrollToTop(); enableAnchor(); }, 800);

          // Observe header height changes right after navigation to keep at top
          try {
            const header = document.querySelector('header') as HTMLElement | null;
            if (header && 'ResizeObserver' in window) {
              const ro = new ResizeObserver(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
              });
              ro.observe(header);
              setTimeout(() => ro.disconnect(), 1500);
            }
          } catch {}
        } else {
          setTimeout(enableAnchor, 800);
        }
      }

      prevPathRef.current = currentFull;
    }

    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [pathname, search, hash]);

  // Handle in-page hash navigation when pathname doesn't change
  useEffect(() => {
    if (!hash) return;
    const element = findHashTarget(hash);
    if (element) {
      const header = document.querySelector('header') as HTMLElement | null;
      const offset = header ? header.offsetHeight + 8 : 0;
      const y = (element as HTMLElement).getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }
  }, [hash]);

  return null;
}
